// =====================================================
// CLINICAL RECORD VIEWER - Visor de Historia Clínica
// Vista detallada con opción de impresión y gestión de adjuntos
// =====================================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Printer,
  Download,
  Trash2,
  Calendar,
  User,
  Clock,
  FileText,
  Paperclip,
  History,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  X,
  Eye,
  Upload,
  Plus,
  Image,
  File,
  // Íconos para plantillas
  Stethoscope,
  ClipboardList,
  Baby,
  Heart,
  Shield,
  Users,
  Activity,
  Pill,
  Syringe,
  Thermometer,
  Brain,
  Bone,
  HeartPulse,
  Share2,
  UserPlus,
  Search,
  Check,
  type LucideIcon
} from 'lucide-react';

import {
  ClinicalRecord,
  ClinicalTemplate,
  RecordAttachment,
  RecordVersion,
  TemplateSection,
  TemplateField,
  SelectOption
} from '../../types/clinicalRecords.types';

import clinicalRecordsApi, { formatRecordDate } from '../../services/clinicalRecords.api';

// =====================================================
// HELPER PARA ÍCONOS DINÁMICOS
// =====================================================

const iconMap: Record<string, LucideIcon> = {
  'stethoscope': Stethoscope,
  'clipboard-list': ClipboardList,
  'baby': Baby,
  'heart': Heart,
  'shield': Shield,
  'users': Users,
  'child': Users,
  'activity': Activity,
  'pill': Pill,
  'syringe': Syringe,
  'thermometer': Thermometer,
  'eye': Eye,
  'brain': Brain,
  'bone': Bone,
  'heart-pulse': HeartPulse,
  'file-text': FileText,
};

interface DynamicIconProps {
  name: string;
  className?: string;
}

const DynamicIcon: React.FC<DynamicIconProps> = ({ name, className = "w-6 h-6" }) => {
  const IconComponent = iconMap[name?.toLowerCase()];
  
  if (IconComponent) {
    return <IconComponent className={className} />;
  }
  
  return <ClipboardList className={className} />;
};

// =====================================================
// COMPONENTE DE SECCIÓN DE VISUALIZACIÓN
// =====================================================

interface ViewSectionProps {
  section: TemplateSection;
  formData: Record<string, any>;
  accentColor?: string;
}

const ViewSection: React.FC<ViewSectionProps> = ({ section, formData, accentColor = '#3B82F6' }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  const getFieldDisplayValue = (field: TemplateField): string => {
    const value = formData?.[field.field_key];
    
    if (value === undefined || value === null || value === '') {
      return '—';
    }
    
    if (field.field_type === 'checkbox' && Array.isArray(value)) {
      return value.join(', ');
    }
    
    if (field.field_type === 'date' && value) {
      return formatRecordDate(value);
    }
    
    if (field.field_type === 'select' || field.field_type === 'radio') {
      if (field.options) {
        const options = field.options as Array<string | SelectOption>;
        const option = options.find((opt: string | SelectOption) => 
          (typeof opt === 'string' ? opt : opt.value) === value
        );
        if (option && typeof option !== 'string') {
          return option.label;
        }
      }
    }
    
    return String(value);
  };
  
  // Filtrar campos que tienen valor - con validación de formData
  const fieldsWithValues = section.fields?.filter(
    (field: TemplateField) => {
      const value = formData?.[field.field_key];
      return value !== undefined && value !== null && value !== '';
    }
  ) || [];
  
  if (fieldsWithValues.length === 0) {
    return null;
  }
  
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 sm:px-6 py-4 flex items-center gap-3 hover:bg-gray-50/70 transition-colors print:hover:bg-transparent text-left"
      >
        {/* Acento de color de la plantilla */}
        <span
          className="w-1.5 h-6 rounded-full flex-shrink-0 print:hidden"
          style={{ backgroundColor: accentColor }}
        />
        <h3 className="font-semibold text-gray-900 text-[15px] flex-1 tracking-tight">
          {section.name}
        </h3>
        <span className="print:hidden text-gray-400">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </span>
      </button>

      {isExpanded && (
        <div className="px-5 sm:px-6 pb-5">
          {section.description && (
            <p className="text-sm text-gray-500 mb-4 -mt-1">{section.description}</p>
          )}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fieldsWithValues
              .sort((a: TemplateField, b: TemplateField) => a.display_order - b.display_order)
              .map((field: TemplateField) => {
                const isBlock = field.field_type === 'textarea' || field.field_type === 'drawing' || field.field_type === 'textarea_with_attachments';
                const isFull = isBlock || field.width === 'full';
                return (
                <div
                  key={field.id}
                  className={`${isFull ? 'sm:col-span-2' : ''} rounded-xl border px-4 py-3 ${
                    isBlock ? 'bg-white border-gray-200' : 'bg-slate-50/70 border-slate-100'
                  }`}
                >
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5 text-left">
                    {field.field_label}
                  </dt>
                  <dd className={`text-left ${
                    field.field_type === 'textarea' || field.field_type === 'textarea_with_attachments'
                      ? 'whitespace-pre-wrap text-[15px] text-gray-900 font-medium leading-relaxed'
                      : 'text-base text-gray-900 font-semibold leading-snug'
                  }`}>
                    {field.field_type === 'drawing' && formData?.[field.field_key] ? (
                      <div className="mt-1">
                        <img 
                          src={formData[field.field_key]} 
                          alt={field.field_label}
                          className="w-full h-auto border border-gray-200 rounded-lg bg-white"
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                        />
                      </div>
                    ) : field.field_type === 'textarea_with_attachments' && formData?.[field.field_key] ? (
                      <div className="space-y-2">
                        {formData[field.field_key]?.text && (
                          <p className="whitespace-pre-wrap">{formData[field.field_key].text}</p>
                        )}
                        {formData[field.field_key]?.files?.length > 0 && (
                          <div className="space-y-1.5 mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs font-medium text-gray-500">Archivos adjuntos:</p>
                            {formData[field.field_key].files.map((file: any, idx: number) => (
                              file.type?.startsWith('image/') ? (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => setPreviewImage(file.url)}
                                  className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-sm w-full text-left"
                                >
                                  <Image className="w-4 h-4 text-green-600 flex-shrink-0" />
                                  <span className="truncate text-blue-700">{file.name}</span>
                                  <Eye className="w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0" />
                                </button>
                              ) : (
                                <a
                                  key={idx}
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-sm"
                                >
                                  <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
                                  <span className="truncate text-blue-700">{file.name}</span>
                                  <Download className="w-3.5 h-3.5 text-gray-400 ml-auto flex-shrink-0" />
                                </a>
                              )
                            ))}
                          </div>
                        )}
                        {!formData[field.field_key]?.text && (!formData[field.field_key]?.files || formData[field.field_key].files.length === 0) && (
                          <span>—</span>
                        )}
                      </div>
                    ) : (
                      getFieldDisplayValue(field)
                    )}
                  </dd>
                </div>
                );
              })}
          </div>
        </div>
      )}
      
      {/* Modal preview de imagen */}
      {previewImage && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute -top-3 -right-3 p-1.5 bg-white rounded-full shadow-lg text-gray-700 hover:text-red-600 z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            <a
              href={previewImage}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-3 right-3 px-3 py-1.5 bg-white/90 hover:bg-white rounded-lg shadow text-sm text-gray-700 flex items-center gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="w-4 h-4" /> Descargar
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================
// COMPONENTE DE HISTORIAL DE VERSIONES
// =====================================================

interface VersionHistoryProps {
  versions: RecordVersion[];
  onViewVersion: (version: RecordVersion) => void;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({ versions, onViewVersion }) => {
  if (!versions || versions.length <= 1) {
    return null;
  }
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <History className="w-5 h-5 text-gray-500" />
          Historial de Versiones
        </h3>
      </div>
      
      <div className="divide-y divide-gray-100">
        {versions.map((version, index) => (
          <div
            key={version.id}
            className="px-6 py-3 flex items-center justify-between hover:bg-gray-50"
          >
            <div>
              <p className="font-medium text-gray-900">
                Versión {version.version_number}
                {index === 0 && (
                  <span className="ml-2 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                    Actual
                  </span>
                )}
              </p>
              <p className="text-sm text-gray-500">
                {new Date(version.created_at).toLocaleString('es-AR')}
                {version.change_reason && ` • ${version.change_reason}`}
              </p>
            </div>
            {index > 0 && (
              <button
                onClick={() => onViewVersion(version)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Ver
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// =====================================================
// COMPONENTE DE ARCHIVOS ADJUNTOS CON GESTIÓN
// =====================================================

interface AttachmentsManagerProps {
  recordId: number;
  attachments: RecordAttachment[];
  onAttachmentsChange: (attachments: RecordAttachment[]) => void;
}

const AttachmentsManager: React.FC<AttachmentsManagerProps> = ({ 
  recordId, 
  attachments, 
  onAttachmentsChange 
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Visualizador in-app: índice del archivo abierto (null = cerrado)
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const isImage = (t: string) => !!t && t.includes('image');
  const isPdf = (t: string) => !!t && t.includes('pdf');
  // Se puede previsualizar dentro de la app (imágenes y PDFs); el resto se abre aparte
  const isPreviewable = (t: string) => isImage(t) || isPdf(t);

  // Navegación por teclado del visor (Esc cierra, flechas cambian de archivo)
  useEffect(() => {
    if (viewerIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setViewerIndex(null);
      else if (e.key === 'ArrowLeft') setViewerIndex(i => (i === null ? i : (i - 1 + attachments.length) % attachments.length));
      else if (e.key === 'ArrowRight') setViewerIndex(i => (i === null ? i : (i + 1) % attachments.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewerIndex, attachments.length]);

  // Obtener ícono según tipo de archivo
  const getFileIcon = (fileType: string) => {
    if (fileType?.includes('image')) return <Image className="w-6 h-6 text-purple-500" />;
    if (fileType?.includes('pdf')) return <FileText className="w-6 h-6 text-red-500" />;
    if (fileType?.includes('word') || fileType?.includes('document')) return <FileText className="w-6 h-6 text-blue-500" />;
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return <FileText className="w-6 h-6 text-green-500" />;
    return <File className="w-6 h-6 text-gray-500" />;
  };

  // Formatear tamaño de archivo
  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Formatear fecha
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Manejar selección de archivo
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      setUploadError('Tipo de archivo no permitido. Formatos aceptados: PDF, JPG, PNG, GIF, WEBP, DOC, DOCX');
      return;
    }

    // Validar tamaño (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('El archivo es demasiado grande. Tamaño máximo: 10MB');
      return;
    }

    setSelectedFile(file);
    setUploadError(null);
    setShowUploadForm(true);
  };

  // Subir archivo
  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadError(null);

    try {
      const response = await clinicalRecordsApi.uploadAttachment(
        recordId,
        selectedFile,
        description || undefined,
        'documento'
      );

      if (response.success && response.attachment) {
        // Agregar el nuevo archivo a la lista
        onAttachmentsChange([...attachments, response.attachment]);
        
        // Limpiar formulario
        setSelectedFile(null);
        setDescription('');
        setShowUploadForm(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (err: any) {
      console.error('Error subiendo archivo:', err);
      setUploadError(err.message || 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  // Eliminar archivo
  const handleDelete = async (attachmentId: number) => {
    if (window.confirm('¿Estás seguro de eliminar este archivo?')) return;

    setDeletingId(attachmentId);

    try {
      await clinicalRecordsApi.deleteAttachment(recordId, attachmentId);
      
      // Remover de la lista
      onAttachmentsChange(attachments.filter(a => a.id !== attachmentId));
    } catch (err: any) {
      console.error('Error eliminando archivo:', err);
      alert('Error al eliminar: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Cancelar subida
  const handleCancelUpload = () => {
    setSelectedFile(null);
    setDescription('');
    setShowUploadForm(false);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden print:hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Paperclip className="w-5 h-5 text-gray-500" />
          Archivos Adjuntos {attachments.length > 0 && `(${attachments.length})`}
        </h3>
        
        {!showUploadForm && (
          <label className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 cursor-pointer transition-colors text-sm font-medium">
            <Plus className="w-4 h-4" />
            Agregar archivo
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
              className="hidden"
            />
          </label>
        )}
      </div>

      {/* Formulario de subida */}
      {showUploadForm && selectedFile && (
        <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-blue-200">
              {getFileIcon(selectedFile.type)}
            </div>
            
            <div className="flex-1">
              <p className="font-medium text-gray-900 truncate">{selectedFile.name}</p>
              <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
              
              <div className="mt-3">
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descripción del archivo (opcional, ej: Resultado de laboratorio)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {uploadError && (
                <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {uploadError}
                </div>
              )}
              
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Subir archivo
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleCancelUpload}
                  disabled={uploading}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista de archivos */}
      {attachments.length > 0 ? (
        <div className="divide-y divide-gray-100">
          {attachments.map(attachment => (
            <div
              key={attachment.id}
              className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors group"
            >
              {/* Miniatura (imagen real) o ícono según tipo */}
              {isImage(attachment.file_type) ? (
                <button
                  type="button"
                  onClick={() => {
                    const idx = attachments.findIndex(a => a.id === attachment.id);
                    setViewerIndex(idx >= 0 ? idx : 0);
                  }}
                  className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200 hover:ring-2 hover:ring-blue-400 transition-all bg-gray-100"
                  title="Ver imagen"
                >
                  <img
                    src={attachment.file_url}
                    alt={attachment.file_name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </button>
              ) : (
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  {getFileIcon(attachment.file_type)}
                </div>
              )}
              
              {/* Info del archivo */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{attachment.file_name}</p>
                <p className="text-sm text-gray-500">
                  {formatFileSize(attachment.file_size)}
                  {attachment.description && ` • ${attachment.description}`}
                  {(attachment.uploaded_at || attachment.created_at) && 
                    ` • ${formatDate(attachment.uploaded_at || attachment.created_at)}`
                  }
                </p>
              </div>
              
              {/* Acciones */}
              <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                {/* Ver — imágenes/PDF en visor propio; otros en pestaña nueva */}
                {isPreviewable(attachment.file_type) ? (
                  <button
                    type="button"
                    onClick={() => {
                      const idx = attachments.findIndex(a => a.id === attachment.id);
                      setViewerIndex(idx >= 0 ? idx : 0);
                    }}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Ver archivo"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                ) : (
                  <a
                    href={attachment.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Abrir archivo"
                  >
                    <Eye className="w-5 h-5" />
                  </a>
                )}

                {/* Descargar */}
                <a
                  href={attachment.file_url}
                  download={attachment.file_name}
                  className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  title="Descargar"
                >
                  <Download className="w-5 h-5" />
                </a>
                
                {/* Eliminar */}
                <button
                  onClick={() => handleDelete(attachment.id)}
                  disabled={deletingId === attachment.id}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Eliminar"
                >
                  {deletingId === attachment.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trash2 className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="px-6 py-8 text-center">
          <Paperclip className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No hay archivos adjuntos</p>
          <p className="text-sm text-gray-400 mt-1">
            Podés agregar estudios, resultados o documentos relevantes
          </p>
        </div>
      )}

      {/* Formatos aceptados */}
      {attachments.length === 0 && !showUploadForm && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
          <p className="text-xs text-gray-500 text-center">
            Formatos aceptados: PDF, JPG, PNG, GIF, WEBP, DOC, DOCX • Máximo 10MB por archivo
          </p>
        </div>
      )}

      {/* Visualizador de archivos in-app (lightbox) */}
      {viewerIndex !== null && attachments[viewerIndex] && (() => {
        const current = attachments[viewerIndex];
        const goPrev = () => setViewerIndex(i => (i === null ? i : (i - 1 + attachments.length) % attachments.length));
        const goNext = () => setViewerIndex(i => (i === null ? i : (i + 1) % attachments.length));
        return (
          <div
            className="fixed inset-0 z-[9999] bg-black/85 flex flex-col animate-backdrop-in"
            onClick={() => setViewerIndex(null)}
          >
            {/* Barra superior */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 text-white" onClick={(e) => e.stopPropagation()}>
              <div className="min-w-0">
                <p className="font-medium truncate">{current.file_name}</p>
                <p className="text-xs text-white/60">
                  {attachments.length > 1 ? `${viewerIndex + 1} de ${attachments.length} • ` : ''}
                  {formatFileSize(current.file_size)}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <a
                  href={current.file_url}
                  download={current.file_name}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                  title="Descargar"
                >
                  <Download className="w-5 h-5" />
                </a>
                <button
                  type="button"
                  onClick={() => setViewerIndex(null)}
                  className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                  title="Cerrar (Esc)"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Contenido */}
            <div className="flex-1 flex items-center justify-center px-4 pb-6 min-h-0" onClick={(e) => e.stopPropagation()}>
              {isImage(current.file_type) ? (
                <img
                  src={current.file_url}
                  alt={current.file_name}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                />
              ) : isPdf(current.file_type) ? (
                <iframe
                  src={current.file_url}
                  title={current.file_name}
                  className="w-full h-full max-w-5xl bg-white rounded-lg shadow-2xl"
                />
              ) : (
                <div className="text-center text-white/80">
                  <File className="w-16 h-16 mx-auto mb-3 opacity-60" />
                  <p>Este archivo no se puede previsualizar.</p>
                  <a
                    href={current.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm"
                  >
                    <Download className="w-4 h-4" /> Descargar para verlo
                  </a>
                </div>
              )}
            </div>

            {/* Flechas de navegación (si hay más de uno) */}
            {attachments.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); goPrev(); }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                  title="Anterior (←)"
                >
                  <ChevronLeft className="w-7 h-7" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); goNext(); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                  title="Siguiente (→)"
                >
                  <ChevronRight className="w-7 h-7" />
                </button>
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const ClinicalRecordViewer: React.FC = () => {
  const navigate = useNavigate();
  const { recordId } = useParams();
  const printRef = useRef<HTMLDivElement>(null);
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [record, setRecord] = useState<ClinicalRecord | null>(null);
  const [template, setTemplate] = useState<ClinicalTemplate | null>(null);
  const [attachments, setAttachments] = useState<RecordAttachment[]>([]);
  const [versions, setVersions] = useState<RecordVersion[]>([]);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<RecordVersion | null>(null);
  
  // Modal de compartir
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareSearch, setShareSearch] = useState('');
  const [shareResults, setShareResults] = useState<any[]>([]);
  const [shareSearching, setShareSearching] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState('');
  const [currentShares, setCurrentShares] = useState<any[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  
  // Cargar datos
  useEffect(() => {
    const loadData = async () => {
      if (!recordId) {
        setError('ID de registro no válido');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // Cargar registro con adjuntos y versiones
        const recordRes = await clinicalRecordsApi.getRecordById(Number(recordId));
        console.log('Record response:', recordRes); // Debug
        
        setRecord(recordRes.record);
        // Los attachments pueden venir dentro de record o como propiedad separada
        setAttachments(recordRes.record?.attachments || recordRes.attachments || []);
        setVersions(recordRes.versions || []);
        
        // Cargar plantilla completa
        if (recordRes.record?.template_id) {
          const templateRes = await clinicalRecordsApi.getTemplateById(recordRes.record.template_id);
          setTemplate(templateRes.template);
        }
        
      } catch (err: any) {
        console.error('Error cargando registro:', err);
        setError(err.message || 'Error al cargar la historia clínica');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [recordId]);
  
  // Handlers
  const handleEdit = () => {
    navigate(`/professional/clinical-records/${recordId}/edit`);
  };
  
  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    if (!record) return;

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/clinical-records/${record.id}/export/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al exportar PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (record.patient_name || 'paciente').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_');
      a.download = `historia_clinica_${safeName}_${record.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exportando PDF:', err);
      alert('Error al exportar historia clínica a PDF');
    }
  };

  const handleDelete = async () => {
    if (!recordId) return;
    
    setDeleting(true);
    try {
      await clinicalRecordsApi.deleteRecord(Number(recordId));
      navigate('/professional/clinical-records');
    } catch (err: any) {
      alert('Error al eliminar: ' + err.message);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };
  
  const handleViewVersion = (version: RecordVersion) => {
    setViewingVersion(version);
  };

  // Handler para actualizar attachments
  const handleAttachmentsChange = (newAttachments: RecordAttachment[]) => {
    setAttachments(newAttachments);
  };

  // === Funciones de compartir ===
  const loadShares = async () => {
    if (!recordId) return;
    setLoadingShares(true);
    try {
      const res = await clinicalRecordsApi.getRecordShares(Number(recordId));
      setCurrentShares(res.shares || []);
    } catch (err) {
      console.error('Error cargando shares:', err);
    } finally {
      setLoadingShares(false);
    }
  };

  const handleShareSearch = async (query: string) => {
    setShareSearch(query);
    if (query.length < 3) {
      setShareResults([]);
      return;
    }
    setShareSearching(true);
    try {
      const res = await clinicalRecordsApi.searchProfessionalsToShare(query);
      // Filtrar profesionales que ya tienen acceso
      const sharedIds = currentShares.map((s: any) => s.shared_with_professional_id);
      setShareResults((res.professionals || []).filter(p => !sharedIds.includes(p.id)));
    } catch (err) {
      console.error('Error buscando profesionales:', err);
    } finally {
      setShareSearching(false);
    }
  };

  const handleShare = async (professionalId: number) => {
    if (!recordId) return;
    setSharing(true);
    try {
      const res = await clinicalRecordsApi.shareRecord(Number(recordId), professionalId, 'full');
      setShareSuccess(res.message || 'Compartido exitosamente');
      setShareSearch('');
      setShareResults([]);
      await loadShares();
      setTimeout(() => setShareSuccess(''), 3000);
    } catch (err: any) {
      alert('Error al compartir: ' + err.message);
    } finally {
      setSharing(false);
    }
  };

  const handleRevokeShare = async (shareId: number) => {
    if (!recordId || !window.confirm('¿Revocar acceso a este profesional?')) return;
    try {
      await clinicalRecordsApi.revokeRecordShare(Number(recordId), shareId);
      await loadShares();
    } catch (err: any) {
      alert('Error: ' + err.message);
    }
  };

  const openShareModal = () => {
    setShowShareModal(true);
    loadShares();
  };
  
  // Obtener los datos del formulario a mostrar
  const displayFormData = viewingVersion 
    ? (viewingVersion.form_data || viewingVersion.changes?.values || {})
    : (record?.form_data || record?.values || {});
  
  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Cargando historia clínica...</p>
        </div>
      </div>
    );
  }
  
  // Error
  if (error || !record) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error al cargar
          </h2>
          <p className="text-gray-600 mb-6">{error || 'No se encontró la historia clínica'}</p>
          <button
            onClick={() => navigate('/professional/clinical-records')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver al listado
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header fijo */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40 print:hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/professional/clinical-records')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                Historia Clínica
              </h1>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={openShareModal}
                className="p-2 text-gray-600 hover:bg-purple-100 hover:text-purple-600 rounded-lg transition-colors"
                title="Compartir"
              >
                <Share2 className="w-5 h-5" />
              </button>
              <button
                onClick={handlePrint}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Imprimir"
              >
                <Printer className="w-5 h-5" />
              </button>
              <button
                onClick={handleExportPDF}
                className="p-2 text-gray-600 hover:bg-green-100 hover:text-green-600 rounded-lg transition-colors"
                title="Exportar PDF"
              >
                <Download className="w-5 h-5" />
              </button>
              <button
                onClick={handleEdit}
                className="p-2 text-gray-600 hover:bg-blue-100 hover:text-blue-600 rounded-lg transition-colors"
                title="Editar"
              >
                <Edit3 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="p-2 text-gray-600 hover:bg-red-100 hover:text-red-600 rounded-lg transition-colors"
                title="Eliminar"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Banner de versión antigua */}
      {viewingVersion && (
        <div className="bg-amber-50 border-b border-amber-200 print:hidden">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <p className="text-amber-800">
                <strong>Viendo versión {viewingVersion.version_number}</strong>
                {' '}del {new Date(viewingVersion.created_at).toLocaleString('es-AR')}
              </p>
              <button
                onClick={() => setViewingVersion(null)}
                className="text-amber-700 hover:text-amber-900 font-medium"
              >
                Ver versión actual
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Banner de historia compartida */}
      {(record as any).is_owner === false && (
        <div className="bg-purple-50 border-b border-purple-200 print:hidden">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-2 text-purple-800">
              <Share2 className="w-4 h-4" />
              <span>
                Historia compartida por <strong>{(record as any).owner_name || 'otro profesional'}</strong>
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Contenido principal */}
      <div ref={printRef} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:py-0 print:px-0">
        {/* Encabezado para impresión */}
        <div className="hidden print:block mb-8 pb-4 border-b-2 border-gray-300">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Historia Clínica</h1>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Paciente:</strong> {record.patient_name}
            </div>
            <div>
              <strong>Fecha:</strong> {formatRecordDate(record.record_date)}
            </div>
            {record.patient_dni && (
              <div>
                <strong>DNI:</strong> {record.patient_dni}
              </div>
            )}
            <div>
              <strong>Tipo:</strong> {template?.name || 'Sin plantilla'}
            </div>
          </div>
        </div>
        
        <div className="space-y-6">
          {/* Card de información del paciente */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden print:shadow-none print:border-gray-300">
            <div
              className="p-6 flex items-start gap-4"
              style={{ borderLeft: `4px solid ${template?.color || '#3B82F6'}` }}
            >
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${template?.color || '#3B82F6'}15`, color: template?.color || '#3B82F6' }}
              >
                <DynamicIcon name={template?.icon || 'clipboard-list'} className="w-7 h-7" />
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{record.patient_name}</h2>
                    <p className="text-gray-500 mt-1">
                      {template?.name || 'Historia Clínica'}
                    </p>
                  </div>
                  {record.version && record.version > 1 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm print:hidden">
                      v{record.version}
                    </span>
                  )}
                </div>
                
                <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {formatRecordDate(record.record_date)}
                  </div>
                  
                  {record.patient_dni && (
                    <div className="flex items-center gap-1.5">
                      <User className="w-4 h-4 text-gray-400" />
                      DNI: {record.patient_dni}
                    </div>
                  )}
                  
                  {record.patient_email && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-400">✉️</span>
                      {record.patient_email}
                    </div>
                  )}
                  
                  {record.patient_phone && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-400">📱</span>
                      {record.patient_phone}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Secciones del formulario */}
          {template?.sections && template.sections.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden print:shadow-none print:border-gray-300">
              {template.sections
                .sort((a: TemplateSection, b: TemplateSection) => a.display_order - b.display_order)
                .map((section: TemplateSection) => (
                  <ViewSection
                    key={section.id}
                    section={section}
                    formData={displayFormData}
                    accentColor={template?.color || '#3B82F6'}
                  />
                ))}
            </div>
          )}
          
          {/* Notas adicionales */}
          {record.notes && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden print:shadow-none print:border-gray-300">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Notas Adicionales</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 whitespace-pre-wrap">{record.notes}</p>
              </div>
            </div>
          )}
          
          {/* Archivos adjuntos - CON GESTIÓN */}
          <AttachmentsManager 
            recordId={Number(recordId)}
            attachments={attachments} 
            onAttachmentsChange={handleAttachmentsChange}
          />
          
          {/* Historial de versiones */}
          <VersionHistory
            versions={versions}
            onViewVersion={handleViewVersion}
          />
          
          {/* Metadatos */}
          <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-500 print:hidden">
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                Creado: {new Date(record.created_at).toLocaleString('es-AR')}
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                Actualizado: {new Date(record.updated_at).toLocaleString('es-AR')}
              </div>
              <div>
                ID: #{record.id}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  Eliminar Historia Clínica
                </h3>
                <p className="mt-2 text-gray-600">
                  ¿Estás seguro de eliminar esta historia clínica de <strong>{record.patient_name}</strong>?
                  Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de compartir */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Compartir Historia</h3>
                  <p className="text-sm text-gray-500">{record.patient_name}</p>
                </div>
              </div>
              <button onClick={() => setShowShareModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Buscador */}
            <div className="p-4 border-b border-gray-100">
              {shareSuccess && (
                <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  {shareSuccess}
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar profesional por nombre o email..."
                  value={shareSearch}
                  onChange={(e) => handleShareSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  autoFocus
                />
                {shareSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-500 animate-spin" />
                )}
              </div>

              {/* Resultados de búsqueda */}
              {shareResults.length > 0 && (
                <div className="mt-2 border border-gray-100 rounded-xl overflow-hidden">
                  {shareResults.map((prof) => (
                    <div key={prof.id} className="flex items-center justify-between p-3 hover:bg-gray-50 border-b border-gray-50 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                          {prof.nombre?.[0] || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{prof.nombre} {prof.apellido}</p>
                          <p className="text-xs text-gray-500">{prof.especialidad || prof.email}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleShare(prof.id)}
                        disabled={sharing}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                      >
                        {sharing ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                        Compartir
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {shareSearch.length >= 3 && shareResults.length === 0 && !shareSearching && (
                <p className="mt-2 text-sm text-gray-400 text-center py-2">No se encontraron profesionales</p>
              )}
            </div>

            {/* Lista de profesionales con acceso */}
            <div className="flex-1 overflow-y-auto p-4">
              <h4 className="text-sm font-medium text-gray-500 mb-3">
                Profesionales con acceso ({currentShares.length})
              </h4>

              {loadingShares ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              ) : currentShares.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Esta historia no está compartida con nadie</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {currentShares.map((share: any) => (
                    <div key={share.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center">
                          {share.picture ? (
                            <img src={share.picture} className="w-9 h-9 rounded-full object-cover" alt="" />
                          ) : (
                            <span className="text-purple-600 font-bold text-sm">{share.nombre?.[0]}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{share.nombre} {share.apellido}</p>
                          <p className="text-xs text-gray-500">{share.especialidad || share.email}</p>
                        </div>
                      </div>
                      {(record as any).is_owner !== false && (
                        <button
                          onClick={() => handleRevokeShare(share.id)}
                          className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                        >
                          Revocar
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Estilos de impresión */}
      <style>{`
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print\\:block {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ClinicalRecordViewer;